import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Users, BookOpen, FileText, CheckCircle, Plus, Activity, Upload, Download, AlertCircle, RefreshCw, Table, Pencil, X, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { ApiService } from '../services/api';
import type { User, SystemStats, Journal, Entry } from '../types';

interface BulkUploadResult {
  total: number;
  success: number;
  failed: number;
  errors: Array<{ row: number; error: string; data: any }>;
}

interface ReportRow {
  student_name: string;
  student_email: string;
  student_usn: string;
  student_program: string;
  student_cohort: string;
  entry_title: string;
  max_score: number;
  submitted_at: string;
  word_count: number;
  student_response: string;
  ai_score: number | string;
  percentage: number | string;
  grade_letter: string;
  llm_confidence: number | string;
  ai_feedback: string;
  strengths: string;
  growth: string;
  teacher_grade: number | string;
  teacher_comments: string;
  status: string;
}

export const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'journals' | 'reports' | 'logs'>('overview');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'student', usn: '', program: '', cohort: '' });
  const [editForm, setEditForm] = useState({ name: '', email: '', role: 'student', usn: '', program: '', cohort: '' });
  const [userFilter, setUserFilter] = useState<string>('');
  const [bulkUploadResult, setBulkUploadResult] = useState<BulkUploadResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState('Student@123');
  const [defaultRole, setDefaultRole] = useState('student');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reports state
  const [journals, setJournals] = useState<Journal[]>([]);
  const [selectedJournalId, setSelectedJournalId] = useState<string>('');
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [reportLoading, setReportLoading] = useState(false);

  // Journals management state
  const [allJournals, setAllJournals] = useState<Journal[]>([]);
  const [journalEntries, setJournalEntries] = useState<Record<string, Entry[]>>({});
  const [expandedJournals, setExpandedJournals] = useState<Set<string>>(new Set());
  const [journalsLoading, setJournalsLoading] = useState(false);
  const [showEditJournal, setShowEditJournal] = useState(false);
  const [editingJournal, setEditingJournal] = useState<Journal | null>(null);
  const [journalEditForm, setJournalEditForm] = useState({ title: '', description: '' });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (activeTab === 'reports' && journals.length === 0) {
      loadJournals();
    }
    if (activeTab === 'journals') {
      loadAllJournals();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedJournalId) {
      loadReportData();
    }
  }, [selectedJournalId]);

  const loadData = async () => {
    try {
      const [statsData, usersData, logsData] = await Promise.all([
        ApiService.getSystemStats(),
        ApiService.getUsers(),
        ApiService.getAuditLogs({ limit: 50 })
      ]);
      setStats(statsData);
      setUsers(usersData);
      setAuditLogs(logsData.logs);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const loadJournals = async () => {
    try {
      const data = await ApiService.getJournals();
      setJournals(data);
      if (data.length > 0 && !selectedJournalId) {
        setSelectedJournalId(data[0].id);
      }
    } catch { toast.error('Failed to load journals'); }
  };

  const loadAllJournals = async () => {
    setJournalsLoading(true);
    try {
      const data = await ApiService.getJournals();
      setAllJournals(data);
    } catch { toast.error('Failed to load journals'); }
    finally { setJournalsLoading(false); }
  };

  const toggleJournalExpand = async (journalId: string) => {
    const next = new Set(expandedJournals);
    if (next.has(journalId)) {
      next.delete(journalId);
    } else {
      next.add(journalId);
      if (!journalEntries[journalId]) {
        try {
          const entries = await ApiService.getEntries(journalId);
          setJournalEntries(prev => ({ ...prev, [journalId]: entries }));
        } catch { toast.error('Failed to load entries'); }
      }
    }
    setExpandedJournals(next);
  };

  const handleEditJournalOpen = (journal: Journal) => {
    setEditingJournal(journal);
    setJournalEditForm({ title: journal.title, description: journal.description || '' });
    setShowEditJournal(true);
  };

  const handleEditJournal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingJournal) return;
    try {
      await ApiService.updateJournal(editingJournal.id, journalEditForm);
      toast.success('Journal updated');
      setShowEditJournal(false);
      setEditingJournal(null);
      loadAllJournals();
    } catch (error: any) { toast.error(error.message || 'Failed to update journal'); }
  };

  const handleDeleteJournal = async (journalId: string, title: string) => {
    if (!confirm(`Delete journal "${title}" and all its entries and submissions? This cannot be undone.`)) return;
    try {
      await ApiService.deleteJournal(journalId);
      toast.success('Journal deleted');
      setAllJournals(prev => prev.filter(j => j.id !== journalId));
    } catch (error: any) { toast.error(error.message || 'Failed to delete journal'); }
  };

  const handleDeleteEntry = async (journalId: string, entryId: string, title: string) => {
    if (!confirm(`Delete entry "${title}" and all its submissions? This cannot be undone.`)) return;
    try {
      await ApiService.deleteEntry(entryId);
      toast.success('Entry deleted');
      setJournalEntries(prev => ({ ...prev, [journalId]: prev[journalId].filter(e => e.id !== entryId) }));
    } catch (error: any) { toast.error(error.message || 'Failed to delete entry'); }
  };

  const loadReportData = async () => {
    if (!selectedJournalId) return;
    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const baseUrl = import.meta.env.VITE_API_URL || 'https://flex-api.iiitdwd.com/api';
      const response = await fetch(`${baseUrl}/journals/${selectedJournalId}/reports/data`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to load report');
      }
      const data = await response.json();
      setReportData(data.data || []);
    } catch (error: any) {
      console.error('Failed to load report data:', error);
      toast.error(error.message || 'Failed to load report data');
    } finally {
      setReportLoading(false);
    }
  };

  const handleGenerateReport = async (format: 'xlsx' | 'csv' = 'csv') => {
    if (!selectedJournalId) return;
    try {
      toast.info(`Generating ${format.toUpperCase()} report...`);
      
      const token = localStorage.getItem('token');
      const baseUrl = import.meta.env.VITE_API_URL || 'https://flex-api.iiitdwd.com/api';
      const response = await fetch(`${baseUrl}/journals/${selectedJournalId}/reports/download?format=${format}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Failed to generate report');
      
      const arrayBuffer = await response.arrayBuffer();
      const mimeType = format === 'xlsx' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8';
      
      const blob = new Blob([arrayBuffer], { type: mimeType });
      const selectedJournal = journals.find(j => j.id === selectedJournalId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `journal_report_${selectedJournal?.title.replace(/[^a-zA-Z0-9]/g, '_') || 'report'}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded!');
    } catch (error) { 
      toast.error('Failed to generate report'); 
      console.error(error);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await ApiService.createUser({
        ...userForm,
        usn: userForm.usn || undefined,
        program: userForm.program || undefined,
        cohort: userForm.cohort || undefined
      });
      toast.success('User created');
      setShowCreateUser(false);
      setUserForm({ name: '', email: '', password: '', role: 'student', usn: '', program: '', cohort: '' });
      loadData();
    } catch (error: any) { toast.error(error.message); }
  };

  const handleEditUserOpen = (user: User) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      usn: user.usn || '',
      program: user.program || '',
      cohort: user.cohort || ''
    });
    setShowEditUser(true);
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await ApiService.updateUser(editingUser.id, {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        usn: editForm.usn || undefined,
        program: editForm.program || undefined,
        cohort: editForm.cohort || undefined
      });
      toast.success('User updated');
      setShowEditUser(false);
      setEditingUser(null);
      loadData();
    } catch (error: any) { toast.error(error.message); }
  };

  const handleToggleStatus = async (userId: string) => {
    try {
      const result = await ApiService.toggleUserStatus(userId);
      toast.success(result.disabled ? 'User disabled' : 'User enabled');
      loadData();
    } catch { toast.error('Failed to update user'); }
  };

  const handleDownloadTemplate = () => {
    const csvContent = `email,name,password,role,usn,program,cohort
student1@example.com,John Doe,password123,student,USN001,Computer Science,2024-CSE-A
student2@example.com,Jane Smith,password456,student,USN002,Electronics,2024-ECE-B
student3@example.com,Bob Wilson,,student,USN003,Mechanical,2024-ME-A
teacher1@example.com,Alice Teacher,teacher789,teacher,,,`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setBulkUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const baseUrl = import.meta.env.VITE_API_URL || 'https://flex-api.iiitdwd.com/api';
      const response = await fetch(
        `${baseUrl}/admin/users/bulk-upload?default_role=${defaultRole}&default_password=${encodeURIComponent(defaultPassword)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result: BulkUploadResult = await response.json();
      setBulkUploadResult(result);
      
      if (result.success > 0) {
        toast.success(`Successfully created ${result.success} users!`);
        loadData();
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} users failed to create`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Bulk upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredUsers = userFilter ? users.filter(u => u.role === userFilter) : users;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-slate-800">Admin Dashboard</h2>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          {(['overview', 'users', 'journals', 'reports', 'logs'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors capitalize ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>{tab}</button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3"><div className="p-3 bg-blue-50 rounded-lg"><Users className="w-6 h-6 text-blue-600" /></div><div><p className="text-3xl font-bold text-slate-800">{stats.users.total}</p><p className="text-sm text-slate-500">Total Users</p></div></div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3"><div className="p-3 bg-purple-50 rounded-lg"><BookOpen className="w-6 h-6 text-purple-600" /></div><div><p className="text-3xl font-bold text-slate-800">{stats.journals}</p><p className="text-sm text-slate-500">Journals</p></div></div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3"><div className="p-3 bg-green-50 rounded-lg"><FileText className="w-6 h-6 text-green-600" /></div><div><p className="text-3xl font-bold text-slate-800">{stats.entries}</p><p className="text-sm text-slate-500">Entries</p></div></div>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3"><div className="p-3 bg-amber-50 rounded-lg"><CheckCircle className="w-6 h-6 text-amber-600" /></div><div><p className="text-3xl font-bold text-slate-800">{stats.submissions.total}</p><p className="text-sm text-slate-500">Submissions</p></div></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" />User Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center"><span className="text-slate-600">Students</span><span className="font-bold text-slate-800">{stats.users.students}</span></div>
                <div className="flex justify-between items-center"><span className="text-slate-600">Teachers</span><span className="font-bold text-slate-800">{stats.users.teachers}</span></div>
                <div className="flex justify-between items-center"><span className="text-slate-600">Admins</span><span className="font-bold text-slate-800">{stats.users.admins}</span></div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Activity className="w-5 h-5 text-green-600" />Submission Status</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center"><span className="text-slate-600">Total</span><span className="font-bold text-slate-800">{stats.submissions.total}</span></div>
                <div className="flex justify-between items-center"><span className="text-slate-600">Graded</span><span className="font-bold text-green-600">{stats.submissions.graded}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
            <div className="flex gap-2">
              {['', 'student', 'teacher', 'admin'].map(role => (
                <button key={role} onClick={() => setUserFilter(role)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${userFilter === role ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{role || 'All'}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowBulkUpload(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg">
                <Upload className="w-5 h-5" />Bulk Upload
              </button>
              <button onClick={() => setShowCreateUser(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">
                <Plus className="w-5 h-5" />New User
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">USN</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Program</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Cohort</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Role</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Created</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{user.name}</td>
                      <td className="px-4 py-3 text-slate-600">{user.email}</td>
                      <td className="px-4 py-3 text-slate-600">{user.usn || <span className="text-slate-300">-</span>}</td>
                      <td className="px-4 py-3 text-slate-600">{user.program || <span className="text-slate-300">-</span>}</td>
                      <td className="px-4 py-3 text-slate-600">{user.cohort || <span className="text-slate-300">-</span>}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : user.role === 'teacher' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{user.role}</span></td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${user.disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{user.disabled ? 'Disabled' : 'Active'}</span></td>
                      <td className="px-4 py-3 text-slate-500 text-sm">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <button onClick={() => handleEditUserOpen(user)} className="px-2 py-1 text-xs font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200">
                          <Pencil className="w-3 h-3 inline mr-1" />Edit
                        </button>
                        <button onClick={() => handleToggleStatus(user.id)} className={`px-3 py-1 text-xs font-medium rounded ${user.disabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>{user.disabled ? 'Enable' : 'Disable'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Journals Tab */}
      {activeTab === 'journals' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-800">All Journals</h3>
            <button onClick={loadAllJournals} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${journalsLoading ? 'animate-spin' : ''}`} />Refresh
            </button>
          </div>
          {journalsLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
          ) : allJournals.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No journals found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allJournals.map(journal => (
                <div key={journal.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                    <button
                      onClick={() => toggleJournalExpand(journal.id)}
                      className="flex items-center gap-2 flex-1 text-left"
                    >
                      {expandedJournals.has(journal.id)
                        ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                        : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                      <BookOpen className="w-4 h-4 text-blue-500 shrink-0" />
                      <div>
                        <span className="font-medium text-slate-800">{journal.title}</span>
                        {journal.description && <span className="ml-2 text-sm text-slate-500">{journal.description}</span>}
                      </div>
                      <span className="ml-3 text-xs text-slate-400">{new Date(journal.created_at).toLocaleDateString()}</span>
                    </button>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEditJournalOpen(journal)}
                        className="px-2 py-1 text-xs font-medium rounded bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center gap-1"
                      >
                        <Pencil className="w-3 h-3" />Edit
                      </button>
                      <button
                        onClick={() => handleDeleteJournal(journal.id, journal.title)}
                        className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />Delete
                      </button>
                    </div>
                  </div>
                  {expandedJournals.has(journal.id) && (
                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
                      {!journalEntries[journal.id] ? (
                        <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-400"></div>Loading entries...
                        </div>
                      ) : journalEntries[journal.id].length === 0 ? (
                        <p className="text-sm text-slate-400 py-2">No entries in this journal</p>
                      ) : (
                        <div className="space-y-2">
                          {journalEntries[journal.id].map(entry => (
                            <div key={entry.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-200">
                              <div className="flex items-center gap-2">
                                <FileText className="w-3.5 h-3.5 text-green-500 shrink-0" />
                                <span className="text-sm font-medium text-slate-700">{entry.title}</span>
                                <span className="text-xs text-slate-400">max {entry.max_score} pts</span>
                                {!entry.is_visible && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">hidden</span>}
                              </div>
                              <button
                                onClick={() => handleDeleteEntry(journal.id, entry.id, entry.title)}
                                className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" />Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* Journal Selector */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Table className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-slate-800">Journal Reports</h3>
              </div>
              <div className="flex items-center gap-3">
                <select 
                  value={selectedJournalId} 
                  onChange={(e) => setSelectedJournalId(e.target.value)}
                  className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-slate-800 font-medium"
                >
                  <option value="">Select a Journal</option>
                  {journals.map(j => (
                    <option key={j.id} value={j.id}>{j.title}</option>
                  ))}
                </select>
                <button onClick={loadReportData} disabled={!selectedJournalId} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg flex items-center gap-2 disabled:opacity-50">
                  <RefreshCw className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                </button>
                <button onClick={() => handleGenerateReport('xlsx')} disabled={!selectedJournalId} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg flex items-center gap-2 disabled:opacity-50">
                  <Download className="w-4 h-4" />Excel
                </button>
                <button onClick={() => handleGenerateReport('csv')} disabled={!selectedJournalId} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg flex items-center gap-2 disabled:opacity-50">
                  <Download className="w-4 h-4" />CSV
                </button>
              </div>
            </div>

            {/* Report Table */}
            {!selectedJournalId ? (
              <div className="text-center py-12 text-slate-500">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Select a journal to view reports</p>
              </div>
            ) : reportLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : reportData.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No submissions yet for this journal</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Student</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">USN</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Program</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Cohort</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Entry</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Submitted</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Words</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">AI Score</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">%</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Grade</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Confidence</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Teacher Grade</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row, idx) => (
                      <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-800">{row.student_name}</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{row.student_email}</td>
                        <td className="px-3 py-2 text-slate-600">{row.student_usn || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{row.student_program || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{row.student_cohort || '-'}</td>
                        <td className="px-3 py-2 text-slate-700">{row.entry_title}</td>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{row.submitted_at || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{row.word_count || '-'}</td>
                        <td className="px-3 py-2 text-slate-800 font-medium">
                          {row.ai_score !== '' ? `${row.ai_score}/${row.max_score}` : '-'}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.percentage ? `${row.percentage}%` : '-'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 text-xs font-bold rounded ${
                            row.grade_letter?.startsWith('A') ? 'bg-green-100 text-green-700' :
                            row.grade_letter?.startsWith('B') ? 'bg-blue-100 text-blue-700' :
                            row.grade_letter?.startsWith('C') ? 'bg-yellow-100 text-yellow-700' :
                            row.grade_letter?.startsWith('D') ? 'bg-orange-100 text-orange-700' :
                            row.grade_letter?.startsWith('F') ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {row.grade_letter || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.llm_confidence ? `${row.llm_confidence}%` : '-'}
                        </td>
                        <td className="px-3 py-2 text-slate-800 font-medium">
                          {row.teacher_grade !== '' ? `${row.teacher_grade}/${row.max_score}` : '-'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            row.status === 'graded' ? 'bg-green-100 text-green-700' :
                            row.status === 'feedback_ready' ? 'bg-blue-100 text-blue-700' :
                            row.status === 'Not Submitted' ? 'bg-slate-100 text-slate-500' :
                            row.status === 'error' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Detailed View */}
          {reportData.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Detailed Feedback View</h3>
              <div className="space-y-4">
                {reportData.filter(r => r.status !== 'Not Submitted').map((row, idx) => (
                  <details key={idx} className="border border-slate-200 rounded-lg">
                    <summary className="px-4 py-3 cursor-pointer hover:bg-slate-50 font-medium text-slate-800">
                      {row.student_name} - {row.entry_title} ({row.grade_letter || 'Not Graded'})
                    </summary>
                    <div className="px-4 py-3 border-t border-slate-200 space-y-3 text-sm">
                      <div className="flex flex-wrap gap-4 text-slate-600">
                        <span><strong>Email:</strong> {row.student_email}</span>
                        {row.student_usn && <span><strong>USN:</strong> {row.student_usn}</span>}
                        {row.student_program && <span><strong>Program:</strong> {row.student_program}</span>}
                        {row.student_cohort && <span><strong>Cohort:</strong> {row.student_cohort}</span>}
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">Student Response:</span>
                        <p className="mt-1 text-slate-600 whitespace-pre-wrap bg-slate-50 p-3 rounded max-h-40 overflow-y-auto">
                          {row.student_response || 'No response'}
                        </p>
                      </div>
                      <div>
                        <span className="font-medium text-slate-700">AI Feedback:</span>
                        <p className="mt-1 text-slate-600 whitespace-pre-wrap bg-blue-50 p-3 rounded">
                          {row.ai_feedback || 'No feedback'}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="font-medium text-slate-700">Strengths:</span>
                          <p className="mt-1 text-green-700 bg-green-50 p-2 rounded">{row.strengths || '-'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-700">Growth Opportunities:</span>
                          <p className="mt-1 text-orange-700 bg-orange-50 p-2 rounded">{row.growth || '-'}</p>
                        </div>
                      </div>
                      {row.teacher_comments && (
                        <div>
                          <span className="font-medium text-slate-700">Teacher Comments:</span>
                          <p className="mt-1 text-slate-600 bg-purple-50 p-3 rounded">{row.teacher_comments}</p>
                        </div>
                      )}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Time</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Action</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Resource</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-sm text-slate-500">{new Date(log.created_at || log.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-slate-800">{log.user_email || log.user_id}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${log.action === 'create' ? 'bg-green-100 text-green-700' : log.action === 'delete' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{log.action}</span></td>
                  <td className="px-4 py-3 text-sm text-slate-600">{log.resource_type || log.entity_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Create User</h3>
                <button onClick={() => setShowCreateUser(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input type="text" required value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" required value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input type="password" required value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg">
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">USN <span className="text-slate-400 text-xs">(optional)</span></label>
                    <input type="text" value={userForm.usn} onChange={(e) => setUserForm({ ...userForm, usn: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="e.g., 1RV21CS001" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Program <span className="text-slate-400 text-xs">(optional)</span></label>
                    <input type="text" value={userForm.program} onChange={(e) => setUserForm({ ...userForm, program: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="e.g., Computer Science" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cohort <span className="text-slate-400 text-xs">(optional)</span></label>
                  <input type="text" value={userForm.cohort} onChange={(e) => setUserForm({ ...userForm, cohort: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="e.g., 2024-CSE-A" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowCreateUser(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Create</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && editingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Edit User</h3>
                <button onClick={() => { setShowEditUser(false); setEditingUser(null); }} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleEditUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input type="text" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" required value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg">
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">USN</label>
                    <input type="text" value={editForm.usn} onChange={(e) => setEditForm({ ...editForm, usn: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="e.g., 1RV21CS001" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Program</label>
                    <input type="text" value={editForm.program} onChange={(e) => setEditForm({ ...editForm, program: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="e.g., Computer Science" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cohort</label>
                  <input type="text" value={editForm.cohort} onChange={(e) => setEditForm({ ...editForm, cohort: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="e.g., 2024-CSE-A" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => { setShowEditUser(false); setEditingUser(null); }} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Bulk Upload Users</h3>
                <button onClick={() => { setShowBulkUpload(false); setBulkUploadResult(null); }} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              
              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">CSV Format</h4>
                  <p className="text-sm text-blue-700 mb-2">Upload a CSV file with columns: <code className="bg-blue-100 px-1 rounded">email, name, password, role, usn, program, cohort</code></p>
                  <p className="text-sm text-blue-700">Password, role, usn, program, and cohort are optional - defaults will be used if not provided.</p>
                  <button onClick={handleDownloadTemplate} className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1">
                    <Download className="w-4 h-4" />Download Template
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Default Role</label>
                    <select value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)} className="w-full px-4 py-2 border border-slate-200 rounded-lg">
                      <option value="student">Student</option>
                      <option value="teacher">Teacher</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Default Password</label>
                    <input type="text" value={defaultPassword} onChange={(e) => setDefaultPassword(e.target.value)} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Upload File</label>
                  <input type="file" ref={fileInputRef} accept=".csv,.xlsx,.xls" onChange={handleBulkUpload} className="w-full px-4 py-2 border border-slate-200 rounded-lg" disabled={uploading} />
                </div>

                {uploading && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Uploading...</span>
                  </div>
                )}

                {bulkUploadResult && (
                  <div className={`p-4 rounded-lg ${bulkUploadResult.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {bulkUploadResult.failed > 0 ? <AlertCircle className="w-5 h-5 text-yellow-600" /> : <CheckCircle className="w-5 h-5 text-green-600" />}
                      <span className="font-medium">{bulkUploadResult.success} of {bulkUploadResult.total} users created</span>
                    </div>
                    {bulkUploadResult.errors.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto">
                        {bulkUploadResult.errors.map((err, i) => (
                          <p key={i} className="text-sm text-red-600">Row {err.row}: {err.error}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
                <button onClick={() => { setShowBulkUpload(false); setBulkUploadResult(null); }} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit Journal Modal */}
      {showEditJournal && editingJournal && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-800">Edit Journal</h3>
                <button onClick={() => { setShowEditJournal(false); setEditingJournal(null); }} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleEditJournal} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                  <input type="text" required value={journalEditForm.title} onChange={(e) => setJournalEditForm({ ...journalEditForm, title: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea rows={3} value={journalEditForm.description} onChange={(e) => setJournalEditForm({ ...journalEditForm, description: e.target.value })} className="w-full px-4 py-2 border border-slate-200 rounded-lg resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setShowEditJournal(false); setEditingJournal(null); }} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
